import { toNumber } from 'lodash';
import queryString from 'query-string';
import { CheckSizeUnitType, parseToPixels, setDPI, SizeUnitType } from '@meitu/saas-editor-core/es/utils/unit-kit';
import {
    CHANGE_DPI_HEIGHT_EDGE,
    CHANGE_DPI_WIDTH_EDGE,
    MAX_STAGE_HEIGHT,
    MAX_STAGE_WIDTH,
} from '@meitu/saas-editor-core/es/version-types';
import { FetchTemplateDetailDetailType, IEditorType, ImageEditorPath, SdkModuleName } from '@/types';
import { filterEmpty } from './track';

export const getParameterValue = (inputString: string, key: string) => {
    // 构造正则表达式，匹配 "key=值" 的模式
    const regex = new RegExp(`${key}=([^&]*)`);

    // 使用正则表达式查找匹配项
    const match = inputString.match(regex);

    // 如果匹配成功，返回匹配的值，否则返回 null
    return match ? decodeURIComponent(match[1]) : '';
};

/**
 * 获取创建空画布参数
 */
export const getQueryCreateEmptyParams = (query = getQuery()) => {
    // const query = getQuery();
    const createEmptyPoster = query.createEmptyPoster;
    let dpi = 72;

    // 移除创建画布参数
    // deleteQueryParams(['createEmptyPoster', 'width', 'height', 'open', 'unit']);
    if (!query.id && createEmptyPoster) {
        const unit = CheckSizeUnitType.isValid(query.unit) ? query.unit : SizeUnitType.px;
        if (unit === SizeUnitType.cm || unit === SizeUnitType.mm) {
            // 此时要看是否修改分辨率
            dpi = 300;
            const sizeWidth = toNumber(query.width) * (unit === SizeUnitType.mm ? 0.1 : 1), //输入框的值
                sizeHeight = toNumber(query.height) * (unit === SizeUnitType.mm ? 0.1 : 1);
            let wDpi = 72,
                hDpi = 72;
            if (sizeWidth > CHANGE_DPI_WIDTH_EDGE || sizeHeight > CHANGE_DPI_HEIGHT_EDGE) {
                wDpi = Math.floor(((CHANGE_DPI_WIDTH_EDGE / sizeWidth) * 300) / 10) * 10;
                hDpi = Math.floor(((CHANGE_DPI_HEIGHT_EDGE / sizeHeight) * 300) / 10) * 10;
                dpi = Math.min(wDpi, hDpi);
            }
            setDPI(dpi);
        }

        const width = Math.min(
            toNumber(query.width) ? parseToPixels(toNumber(query.width), unit) : 1000,
            MAX_STAGE_WIDTH,
        );

        const height = Math.min(
            toNumber(query.height) ? parseToPixels(toNumber(query.height), unit) : 1000,
            MAX_STAGE_HEIGHT,
        );

        // 功能  打开改图、扩图等插件
        const func = query.feature;
        // extraInfo为官网跳过来时携带，后续可以去除预计4月下旬
        const createInfo = decodeURIComponent(query.createInfo || query.extraInfo || '');

        console.log('resolution params', {
            unit,
            width,
            height,
            dpi,
        });
        const isPPT = query.isPPT;
        return {
            unit,
            width,
            height,
            func,
            imgUrl: decodeURIComponent((query.open as string) || ''),
            createInfo: createInfo ? JSON.parse(createInfo) : null,
            dpi,
            isPPT,
        };
    }

    return undefined;
};

/**
 * 获取数据类型
 */
export const getQueryFetchType = () => {
    const queryType = getQueryParam('type', FetchTemplateDetailDetailType.TYPE_TEMPLATE);
    // 获取类型
    let type = FetchTemplateDetailDetailType.TYPE_TEMPLATE;
    if (Object.values(FetchTemplateDetailDetailType).includes(queryType as FetchTemplateDetailDetailType)) {
        type = queryType as FetchTemplateDetailDetailType;
    }

    return type;
};

/**
 * 获取地址参数对象
 */
export const getQuery = () => {
    return queryString.parse(window.location.search) as { [key: string]: string };
};

/**
 * 获取指定的参数
 *
 * @param key - 参数键名
 * @param defaultValue - 默认值
 * @param query - 请求参数对象
 */
export const getQueryParam = (key: string, defaultValue?: unknown, query?: { [key: string]: unknown }) => {
    const q = query || getQuery();
    return (q[key] || defaultValue) as string;
};

/**
 * 移除 URL 链接上的参数
 *
 * @param params - 参数名称
 */
export const deleteQueryParams = (params: string[] | string) => {
    const query = getQuery();
    if (Array.isArray(params)) {
        params.forEach((key: string) => {
            delete query[key];
        });
    } else {
        delete query[params];
    }
    window.history.replaceState({}, '', getNewUrl(query));
};

/**
 * 向地址栏追加参数
 *
 * @param params - 请求参数
 * @param replace - 参数存在是否替换；默认值为 `true`
 */
export const appendQueryParams = (params: { [key: string]: unknown }, replace = true) => {
    const query = getQuery();
    Object.keys(params).forEach((key) => {
        const val = params[key];
        if (replace) {
            query[key] = val as string;
        } else {
            if (!query[key]) {
                query[key] = val as string;
            }
        }
    });
    replaceLocation(query);
};

/**
 * 替换 URL 地址
 *
 * @param params - 请求参数
 */
export function replaceLocation(params: { [key: string]: unknown }) {
    const query = getQuery();
    const finalQuery = filterEmpty({ ...query, ...params });
    window.history.replaceState({}, '', getNewUrl(finalQuery));
}

/**
 * 获取当前 path 下，指定参数的新地址
 *
 * @param params - 请求参数
 */
export function getNewUrl(params: { [key: string]: unknown }) {
    return `${window.location.origin}${window.location.pathname}${
        Object.keys(params).length
            ? '?' + queryString.stringify(params)
            : window.location.pathname.endsWith('/')
              ? ''
              : '/'
    }`;
}

/**
 * 跳转到指定的编辑器
 *
 * @param params - 请求参数
 */
export function jump2Editor(params: { [key: string]: unknown }) {
    const editorPath = params.editorType === IEditorType.imageEditor ? ImageEditorPath : '/design';
    delete params.editorType;
    return `${window.location.origin}${editorPath}/${
        Object.keys(params).length ? '?' + queryString.stringify(params) : ''
    }`;
}

export const checkIsInSdk = () => {
    const origin = queryString.parse(window.location.search)?.origin;
    return (
        (origin === SdkModuleName.poster || origin === SdkModuleName.template || origin === SdkModuleName.image) &&
        window.self !== window.top
    );
};

/**
 * 移除URL链接上的参数
 * @param params {string[] | string} 参数名称
 */
export const deleteAllQueryParams = () => {
    const query = getQuery();
    const params = Object.keys(query);
    params.forEach((key: any) => {
        delete query[key];
    });
    window.history.replaceState({}, '', getNewUrl(query));
};

export const removeAllQueryAndReplace = (id: string, obj: { [key: string]: string }) => {
    const from = getQueryParam('from');
    deleteAllQueryParams();
    appendQueryParams({
        id,
        mode: 'designer',
        ...obj,
        from,
    });
};

export const removeAllQueryAndAddId = (id: string, isTexMaterialTemplate = false, isUploadWatermark = false) => {
    const from = getQueryParam('from');
    const draft = getQueryParam('draft');
    const template_id = getQueryParam('template_id');
    const parent_id = getQueryParam('parent_id');
    const isIntl = getQueryParam('isIntl');
    deleteAllQueryParams();
    appendQueryParams({
        id,
        mode: 'designer',
        isTexMaterialTemplate: isTexMaterialTemplate ? '1' : undefined,
        isUpdateWatermark: isUploadWatermark ? '1' : undefined,
        from,
        draft: draft,
        old_template_id: template_id,
        parent_id: parent_id,
        isIntl,
    });
};
